import { z } from 'zod';
// ---------------------------------------------------------------------------
// Visit personnel registration (Domain 4 — Cockpit, REQ-VIS-027/028, issue #32)
// ---------------------------------------------------------------------------
// The separate register of people who performed ONE patient visit (legacy
// `Visit_personel` intent): one row per person+role at the visit, feeding the
// "who performed visits in a period" listings and D6 personnel settlements.
// Roles offered at registration are ONLY the site-staff roles declared in the
// trial (`trial_roles.site_staff = true`) and people come ONLY from ACTIVE
// `trial_personnel` assignments of the visit's own trial (D2 boundary — the
// service reads them through the D2 contracts, never a cross-cluster join).
/**
 * One entry of the PUT body — a `trial_personnel` assignment (person + role
 * in the trial) to register at the visit.
 *
 * The role is NOT carried separately: a `trial_personnel` row already IS
 * person+role+trial, and the same person under two roles arrives as two
 * distinct ids (spec scenario `same-person-multiple-roles`). All people under
 * one role are EQUAL — there is no "main person" flag (binding decision of
 * 2026-11-01: "równorzędne").
 */
export const VisitPersonnelItemRequestSchema = z.object({
    trialPersonnelId: z.number().int().positive(),
});
/**
 * Body of `PUT /api/v1/visits/:visitId/personnel` — FULL replacement of the
 * personnel registered for one visit (set-replace, same stance as the visit's
 * kits). An empty `items` array clears the register — that is how the UI
 * saves a registration without personnel (REQ-VIS-027: registering people is
 * optional) and how an edit removes everyone.
 *
 * Any number of people is allowed ("dowolna ilość osób przy jednej roli",
 * 2026-10-24), so there is deliberately no `.max()` cap. Duplicate
 * assignments are rejected as a client bug — the DB unique index
 * (tenant, visit, assignment) would fail the whole transaction anyway.
 *
 * Failure codes (409, current-state conflicts): `VISIT_PERSONNEL_INACTIVE`
 * (assignment deactivated or past its end date), `VISIT_PERSONNEL_EXTERNAL_ROLE`
 * (role is not site staff), `VISIT_PERSONNEL_CROSS_TRIAL` (assignment belongs
 * to another trial than the visit's enrollment). Missing visit/assignment is
 * `NOT_FOUND`, a foreign tenant is `TENANT_MISMATCH`.
 */
export const SaveVisitPersonnelRequestSchema = z.object({
    items: z.array(VisitPersonnelItemRequestSchema).check(z.refine((items) => new Set(items.map((item) => item.trialPersonnelId)).size === items.length, { message: 'Lista zawiera zduplikowane przypisania personelu' })),
});
/**
 * One registered person of a visit, expanded with the role name and the
 * person's name so the visit card (spec scenario `visible-in-visit-card`)
 * renders without any cross-cluster fetch.
 *
 * Names are nullable exactly like in `PersonnelDetailsDto` — the PII columns
 * are ciphertext at rest (ADR-012) and nullable in the DB.
 */
export const VisitPersonnelItemDtoSchema = z.object({
    /** `patient_visit_personnel.id` — the register row. */
    id: z.number().int().positive(),
    patientVisitId: z.number().int().positive(),
    trialPersonnelId: z.number().int().positive(),
    roleId: z.number().int().positive(),
    /** `trial_roles.name` — the role this person performed at the visit. */
    roleName: z.string().min(1),
    personnelId: z.number().int().positive(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
});
/**
 * Response of `GET /api/v1/visits/:visitId/personnel`. The service sorts
 * deterministically (role name, then person name); the contract does not
 * re-sort. `items: []` is the normal state of a visit without registered
 * personnel — registration of people is optional (REQ-VIS-027).
 */
export const VisitPersonnelResponseSchema = z.object({
    visitId: z.number().int().positive(),
    items: z.array(VisitPersonnelItemDtoSchema),
});
//# sourceMappingURL=visit-personnel.schema.js.map